/**
 * The conversion package, as the AutoCAD connector reads it.
 *
 * This is the contract between the API and `AEFI` - a different pair from job-contract's
 * API-to-worker contract, which is why it lives in its own module. The authority for the
 * shape is `ConversionProject` in
 * services/recognition-worker/AEFloorPlan.Contracts/ConversionModels.cs; this mirrors it,
 * and the two have to move together.
 *
 * Field names are camelCase. The connector deserializes with
 * `PropertyNameCaseInsensitive = true` (ConnectorCommands.ImportConversionPackage), so
 * either casing loads, and camelCase is what the rest of this workspace already produces.
 *
 * **What is deliberately absent is as much of the contract as what is present.** Every
 * omitted property keeps the default declared on the C# class, which is the only place those
 * defaults should live:
 *
 * - `standards` - the AE layer names and default wall width. `StandardsData` declares them
 *   and nothing in the web application varies them, so restating them here would create a
 *   second source of truth for a value that already has one.
 * - `templatePath`, `roomBlockPath`, `tlxBlockPath`, `ae03TemplatePath` and the three
 *   `door36*BlockPath` properties - absolute local paths to a DWT and the CAD blocks. A
 *   server has no `Assets` directory, and inventing a path that resolves on nobody's machine
 *   is worse than omitting it: the connector's `ResolveTemplatePath` and
 *   `ResolveRoomBlockPath` already fall back to `AeCadStandardPaths` on the company share
 *   when the packaged path is missing. PROCESSING_PIPELINE.md's "Package and connector"
 *   section calls these a desktop artifact "being removed in the hosted model"; omitting
 *   them is that removal.
 * - `state` and `country` are optional: the web form collects them in step 3 and the project
 *   package carries them, as the desktop writer does.
 */

import type { SheetPlan } from "@plan-forge/sheet-planner";

/** Matches OutputType in ConversionModels.cs. The connector switches on the string. */
export type PackageOutputType = "DwgFloorPlan" | "EpicMap";

export interface PackagePoint {
  readonly x: number;
  readonly y: number;
}

/**
 * The scale, and the measurement it came from.
 *
 * The connector reads exactly two of these - `inchesPerPreviewPixel` and
 * `previewHeightPixels` (ConnectorCommands.cs, `ResolveCalibration` and its two callers).
 * The rest is provenance for a person: which two points were clicked, what distance was
 * typed, and how many pixels apart they were. It is carried because a package is the record
 * of a conversion and "the drafter measured a 3'-0" door here" is the part of that record a
 * human can check.
 */
export interface PackageCalibration {
  readonly point1: PackagePoint;
  readonly point2: PackagePoint;
  readonly previewDistancePixels: number;
  /** As the operator typed it, e.g. `3'-0"`. */
  readonly enteredDistance: string;
  readonly actualDistanceInches: number;
  readonly inchesPerPreviewPixel: number;
  readonly previewWidthPixels: number;
  readonly previewHeightPixels: number;
}

/** Matches SelectionData. `mode` is always "Polygon": whole-page conversion is not supported. */
export interface PackageSelection {
  readonly mode: "Polygon";
  readonly polygon: readonly PackagePoint[];
  readonly previewWidthPixels: number;
  readonly previewHeightPixels: number;
}

export interface PackagePage {
  readonly pageNumber: number;
  /** 0-based floor on this page. Several entries may share a page number, one per floor. */
  readonly areaIndex: number;
  /** Absent when the page borrows another page's scale; see `inheritsCalibrationFromPage`. */
  readonly calibration?: PackageCalibration;
  /** The page whose own calibration this one uses. The connector reads that entry's scale. */
  readonly inheritsCalibrationFromPage?: number;
  /** This page is one piece of a floor split across PDF pages: no keyplan for that floor. */
  readonly skipKeyplan?: boolean;
  readonly conversionArea: PackageSelection;
  /**
   * The engine's own output, passed through byte for byte.
   *
   * `unknown` on purpose. The worker writes a serialized `RecognitionResult` to S3 and this
   * embeds that object without touching it, so every field the connector reads arrives
   * whether or not TypeScript has heard of it. Restating the result's shape here would let a
   * field be dropped by omission - the one failure mode that matters, because the drafter
   * would get a drawing that is missing geometry rather than an error.
   */
  readonly recognition: unknown;
  /**
   * Which part of the building each 03 sheet shows, the matchlines between them and the
   * keyplan hatch. Absent when the page has no walls to plan from, and on packages from before
   * schema 41; the connector falls back to tiling the extents on a grid.
   */
  readonly sheetPlan?: SheetPlan;
}

/**
 * Schema 41.
 *
 * 39 was the desktop application's package. 40 adds `reviewedBy` and `reviewedAtUtc`, which
 * record the human QA step PROCESSING_PIPELINE.md requires before a package exists. 41 adds
 * each page's `sheetPlan`. System.Text.Json ignores properties it has no member for, so a
 * schema-41 package still loads in an older connector, which tiles the old way.
 */
export const PACKAGE_SCHEMA_VERSION = 41;

export interface ConversionPackage {
  readonly schemaVersion: typeof PACKAGE_SCHEMA_VERSION;
  readonly createdUtc: string;
  /**
   * The file the operator opened, as a bare file name. Traceability only - the connector uses
   * it solely as a fallback for naming the output when `schoolName` is blank, and packaging
   * requires a school name, so that fallback never runs here.
   */
  readonly sourceFile: string;
  /** "PDF", "PNG" or "JPG", matching what the desktop writer puts here. */
  readonly sourceKind: string;
  readonly schoolName: string;
  readonly schoolAddress: string;
  readonly districtName: string;
  readonly districtAddress: string;
  readonly portalId: string;
  /** USPS code. Names the 03 drawing, so the desktop application always wrote it. */
  readonly state?: string;
  readonly country?: string;
  /** Ten business days out, as the desktop application computes it. Feeds the TLX title block. */
  readonly titleBlockDueDateUtc: string;
  readonly outputType: PackageOutputType;
  readonly defaultCalibrationSourcePage: number;
  readonly pages: readonly PackagePage[];
  /**
   * Who inspected the recognised geometry, and when.
   *
   * A UPN where the reviewer's token carries one, otherwise the token subject; an
   * unattributable approval is refused rather than recorded blank. Both are absent together
   * on a vector page whose optional review was skipped - a timestamp with nobody attached to
   * it would read as a review that happened.
   */
  readonly reviewedBy: string;
  readonly reviewedAtUtc?: string;
}

/**
 * The file name a drafter sees, matching what the desktop application saves.
 *
 * `X1 - <school>.aefloor.json`, sanitized the way the connector's own `SanitizeFileName`
 * does it: every character Windows rejects in a file name becomes a dash, and a name that
 * sanitizes away to nothing becomes "School". Matching it matters because a drafter moving
 * between the two applications is looking for the same file name, and because `AEFI` derives
 * the output drawing's name from the school name in the package regardless.
 */
export function packageFileName(schoolName: string): string {
  // Path.GetInvalidFileNameChars() on Windows: nine printable characters plus 0x00-0x1f.
  const cleaned = schoolName.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-").trim();
  return `X1 - ${cleaned === "" ? "School" : cleaned}.aefloor.json`;
}
